/**
 * SitePick front — onboarding フォーム（Phase 7.5 / スキーマ駆動）
 *
 * JS 無効でも素の <form>（POST /onboarding/）で動く。ここはその上に:
 *  - repeater の行追加 / 削除（min/max 尊重）
 *  - 「一時保存」を非同期 POST /onboarding/（Accept: application/json）
 *  - 「入力を確定する」を confirm + 非同期送信、サーバの required 検証エラーを表示
 * 保存 / 検証はサーバの OnboardingService（schema allowlist）。token は body に入れない。
 */

/**
 * repeater 初期化。
 * @param {HTMLElement} form
 */
function initRepeaters(form) {
  form.querySelectorAll(".js-sp-repeater").forEach(function (rep) {
    const itemsWrap = rep.querySelector(".js-sp-rep-items");
    const tpl = rep.querySelector(".js-sp-rep-tpl");
    const addBtn = rep.querySelector(".js-sp-rep-add");
    const max = parseInt(rep.getAttribute("data-sp-rep-max"), 10) || 50;
    const min = parseInt(rep.getAttribute("data-sp-rep-min"), 10) || 0;
    let seq = itemsWrap ? itemsWrap.children.length : 0;

    function renumber() {
      const items = itemsWrap ? itemsWrap.querySelectorAll(".p-onboarding__rep-item") : [];
      items.forEach(function (item, i) {
        const num = item.querySelector(".js-sp-rep-num");
        if (num) {
          num.textContent = String(i + 1);
        }
      });
      if (addBtn) {
        addBtn.disabled = items.length >= max;
      }
    }

    function bindRemove(item) {
      const btn = item.querySelector(".js-sp-rep-remove");
      if (!btn) {
        return;
      }
      btn.addEventListener("click", function () {
        const count = itemsWrap.querySelectorAll(".p-onboarding__rep-item").length;
        if (count <= min || count <= 1) {
          item.querySelectorAll("input, textarea, select").forEach(function (el) {
            if (el.type === "checkbox" || el.type === "radio") {
              el.checked = false;
            } else {
              el.value = "";
            }
          });
          return;
        }
        item.remove();
        renumber();
      });
    }

    if (itemsWrap) {
      itemsWrap.querySelectorAll(".p-onboarding__rep-item").forEach(bindRemove);
    }

    if (addBtn && tpl && itemsWrap) {
      addBtn.addEventListener("click", function () {
        if (itemsWrap.querySelectorAll(".p-onboarding__rep-item").length >= max) {
          return;
        }
        seq += 1;
        const html = tpl.innerHTML.replace(/__i__/g, "r" + Date.now() + seq);
        const frag = document.createElement("div");
        frag.innerHTML = html;
        const item = frag.firstElementChild;
        itemsWrap.appendChild(item);
        bindRemove(item);
        renumber();
        const first = item.querySelector("input, textarea, select");
        if (first) {
          first.focus();
        }
      });
    }

    renumber();
  });
}

/**
 * バイト数を読みやすい単位にする。
 * @param {number} bytes
 * @returns {string}
 */
function formatBytes(bytes) {
  if (!bytes || bytes < 1024) {
    return (bytes || 0) + " B";
  }
  const kb = bytes / 1024;
  if (kb < 1024) {
    return Math.round(kb) + " KB";
  }
  return (kb / 1024).toFixed(1) + " MB";
}

/**
 * ファイル選択時に、選んだ画像 / PDF をその場でプレビュー表示する。
 * 送信前のクライアント側プレビューなので、サーバーには何も送らない。
 * repeater で後から増えた行にも効くよう、フォームへの change 委譲で処理する。
 * @param {HTMLFormElement} form
 * @returns {void}
 */
function initFilePreviews(form) {
  form.addEventListener("change", function (e) {
    const input = e.target;
    if (!(input instanceof HTMLInputElement) || input.type !== "file") {
      return;
    }

    let wrap = input.nextElementSibling;
    if (!wrap || !wrap.classList.contains("js-sp-file-pick")) {
      wrap = document.createElement("span");
      wrap.className = "p-onboarding__file-pick js-sp-file-pick";
      input.insertAdjacentElement("afterend", wrap);
    }

    (wrap._spUrls || []).forEach(function (u) {
      URL.revokeObjectURL(u);
    });
    wrap._spUrls = [];
    wrap.textContent = "";

    const files = input.files ? Array.prototype.slice.call(input.files) : [];
    if (!files.length) {
      wrap.hidden = true;
      return;
    }
    wrap.hidden = false;

    files.forEach(function (file) {
      const item = document.createElement("span");
      item.className = "p-onboarding__file-pick-item";

      if (file.type.indexOf("image/") === 0) {
        const url = URL.createObjectURL(file);
        wrap._spUrls.push(url);
        const img = document.createElement("img");
        img.className = "p-onboarding__file-pick-img";
        img.src = url;
        img.alt = "";
        img.addEventListener("load", function () {
          URL.revokeObjectURL(url);
        });
        item.appendChild(img);
      } else {
        const badge = document.createElement("span");
        badge.className = "p-onboarding__file-pick-badge";
        badge.textContent = file.type === "application/pdf" ? "PDF" : "FILE";
        item.appendChild(badge);
      }

      const cap = document.createElement("span");
      cap.className = "p-onboarding__file-pick-cap";
      cap.textContent = file.name + "（" + formatBytes(file.size) + "）";
      item.appendChild(cap);

      wrap.appendChild(item);
    });
  });
}

/**
 * フォームに未送信のファイルがあるか。
 * @param {HTMLElement} form
 * @returns {boolean}
 */
function hasFiles(form) {
  let found = false;
  form.querySelectorAll("input[type='file']").forEach(function (input) {
    if (input.files && input.files.length) {
      found = true;
    }
  });
  return found;
}

/**
 * fields[...] を入れ子オブジェクトへ収集する（JSON 経路用）。
 * @param {HTMLElement} form
 * @returns {Object}
 */
function collectFields(form) {
  const out = {};
  form.querySelectorAll("[name^='fields[']").forEach(function (el) {
    if (el.type === "file") {
      return;
    }
    if ((el.type === "checkbox" || el.type === "radio") && !el.checked) {
      if (el.type === "checkbox" && el.value === "__clear__") {
        return;
      }
      return;
    }
    const path = el.getAttribute("name").slice("fields".length);
    const keys = [];
    path.replace(/\[([^\]]*)\]/g, function (m, k) {
      keys.push(k);
      return "";
    });
    const isArr = keys.length && keys[keys.length - 1] === "";
    if (isArr) {
      keys.pop();
    }
    let node = out;
    for (let i = 0; i < keys.length - 1; i++) {
      node[keys[i]] = node[keys[i]] || {};
      node = node[keys[i]];
    }
    const last = keys[keys.length - 1];
    if (isArr) {
      node[last] = node[last] || [];
      node[last].push(el.value);
    } else {
      node[last] = el.value;
    }
  });
  return out;
}

/**
 * @param {HTMLElement|null} statusEl
 * @param {string} msg
 * @param {boolean} [isError]
 */
function setStatus(statusEl, msg, isError) {
  if (!statusEl) {
    return;
  }
  statusEl.textContent = msg;
  statusEl.classList.toggle("has-error", isError === true);
}

/**
 * @param {HTMLElement|null} listEl
 * @param {string[]} errors
 */
function showErrors(listEl, errors) {
  if (!listEl) {
    return;
  }
  listEl.innerHTML = "";
  if (!errors || !errors.length) {
    listEl.hidden = true;
    return;
  }
  errors.forEach(function (e) {
    const li = document.createElement("li");
    li.textContent = e;
    listEl.appendChild(li);
  });
  listEl.hidden = false;
  listEl.scrollIntoView({ block: "nearest" });
}

/** 一時保存（multipart）でファイルが受け付けられなかった理由を、リロードをまたいで表示するためのキー。 */
const FILE_ERROR_KEY = "sitepick_ob_file_errors";

/**
 * 直前の一時保存で受け付けられなかったファイルの理由を表示する（あれば）。
 * @param {HTMLElement|null} statusEl
 * @param {HTMLElement|null} errEl
 * @returns {void}
 */
function restoreFileErrors(statusEl, errEl) {
  let msg = "";
  try {
    msg = window.sessionStorage.getItem(FILE_ERROR_KEY) || "";
    window.sessionStorage.removeItem(FILE_ERROR_KEY);
  } catch {
    return;
  }
  if (!msg) {
    return;
  }
  setStatus(statusEl, "一部のファイルを保存できませんでした（" + msg + "）。他の入力内容は保存されています。", true);
  showErrors(errEl, msg.split(" / "));
}

/**
 * @param {HTMLFormElement} form
 * @param {boolean} finalize
 * @param {HTMLElement|null} statusEl
 * @param {HTMLElement|null} errEl
 */
async function send(form, finalize, statusEl, errEl) {
  // POST 先は現在の署名付き URL（フォーム action = Router が渡す）。無ければ今のページ URL。
  const endpoint = form.getAttribute("action") || window.location.pathname;
  const nonceEl = form.querySelector("input[name='_sitepick_ob_nonce']");
  const nonce = nonceEl ? nonceEl.value : "";
  const multipart = hasFiles(form);
  const headers = { Accept: "application/json", "X-SitePick-Onboarding-Nonce": nonce };
  let body;

  const confEl = form.querySelector("input[name='confirmation_email']");
  const confEmail = confEl ? confEl.value : "";

  if (multipart) {
    body = new FormData(form);
    body.delete("finalize");
    body.delete("_sitepick_ob_nonce");
    if (finalize) {
      body.append("finalize", "1");
    }
  } else {
    headers["Content-Type"] = "application/json";
    body = JSON.stringify({
      fields: collectFields(form),
      confirmation_email: confEmail,
      finalize: finalize === true
    });
  }

  setStatus(statusEl, finalize ? "確定しています…" : "保存しています…");
  showErrors(errEl, []);

  try {
    const res = await fetch(endpoint, { method: "POST", credentials: "same-origin", headers: headers, body: body });

    if (res.status === 409) {
      window.location.reload();
      return;
    }
    if (res.status === 429) {
      setStatus(statusEl, "短時間に繰り返し保存はできません。少し時間をおいてください。", true);
      return;
    }
    if (res.status === 401) {
      setStatus(statusEl, "セッションの有効期限が切れました。メールのリンクを開き直してください。", true);
      return;
    }

    let data = {};
    try {
      data = await res.json();
    } catch {
      data = {};
    }

    if (res.status === 422) {
      setStatus(statusEl, "未入力の必須項目があります。", true);
      showErrors(errEl, data.errors || []);
      return;
    }
    if (!res.ok) {
      setStatus(statusEl, "保存できませんでした。時間をおいて再度お試しください。", true);
      return;
    }

    if (finalize || data.submitted) {
      window.location.reload();
      return;
    }
    if (multipart) {
      // ファイルの反映を表示するためリロード。受け付けられなかったファイルがあれば、その理由を
      // リロード後に表示する（以前はここで遷移してエラーが消え、「保存したのに出ない」に見えていた）。
      const fileErrors = data.file_errors ? Object.values(data.file_errors) : [];
      if (fileErrors.length) {
        try {
          window.sessionStorage.setItem(FILE_ERROR_KEY, fileErrors.join(" / "));
        } catch {
          // sessionStorage 不可の環境ではリロード後の表示を諦める（保存自体は成功している）。
        }
      }
      window.location.href = endpoint + "?saved=1";
      return;
    }

    const now = new Date();
    const hh = String(now.getHours()).padStart(2, "0");
    const mm = String(now.getMinutes()).padStart(2, "0");
    if (data.file_errors && Object.keys(data.file_errors).length) {
      setStatus(statusEl, "一部のファイルを保存できませんでした（" + Object.values(data.file_errors).join(" / ") + "）", true);
    } else {
      setStatus(statusEl, "一時保存しました（" + hh + ":" + mm + "）");
    }
  } catch {
    setStatus(statusEl, "通信に失敗しました。入力内容はそのままに、もう一度お試しください。", true);
  }
}

/**
 * 初期化。
 * @returns {void}
 */
export function initOnboarding() {
  const form = document.querySelector(".js-sp-onboarding");
  if (!(form instanceof HTMLFormElement)) {
    return;
  }
  initRepeaters(form);
  initFilePreviews(form);

  const statusEl = form.querySelector("[data-sp-ob-status]");
  const errEl = form.querySelector(".js-sp-ob-errors");
  restoreFileErrors(statusEl, errEl);
  const saveBtn = form.querySelector(".js-sp-ob-save");
  const finalizeBtn = form.querySelector(".js-sp-ob-finalize");
  let busy = false;

  if (saveBtn) {
    saveBtn.addEventListener("click", function (e) {
      e.preventDefault();
      if (busy) {
        return;
      }
      busy = true;
      send(form, false, statusEl, errEl).finally(function () {
        busy = false;
      });
    });
  }

  if (finalizeBtn) {
    finalizeBtn.addEventListener("click", function (e) {
      const msg = finalizeBtn.getAttribute("data-sp-confirm") || "確定しますか？";
      e.preventDefault();
      if (!window.confirm(msg) || busy) {
        return;
      }
      busy = true;
      send(form, true, statusEl, errEl).finally(function () {
        busy = false;
      });
    });
  }
}
